import React, { useContext, useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';

import { Listings_ListingData } from '../types/graph/Listings_ListingData';
import ListingGrid from './Listings/ListingGrid';

import getModalContext from '../context/getModalContext';
import { MailIcon, PlusIcon } from '@heroicons/react/outline';
import { Listings_AccountData } from '../types/graph/Listings_AccountData';
import { Listings_AccountStatsData } from '../types/graph/Listings_AccountStatsData';
import { AuthContext } from '../context/AuthContext';

const Listings: React.FC = () => {
  const { getAccountId } = useContext(AuthContext);
  const modals = useContext(getModalContext());
  const [canCreateListing, setCanCreateListing] = useState<boolean>(false);

  const { data, loading, error, refetch } =
    useQuery<Listings_ListingData>(LISTINGS_QUERY);

  const { data: accountData, refetch: accountDataRefetch } =
    useQuery<Listings_AccountData>(GET_ACCOUNT, {
      variables: { accountId: getAccountId() },
    });
  const { data: accountStatsData, refetch: accountStatsDataRefetch } =
    useQuery<Listings_AccountStatsData>(GET_ACCOUNT_STATS);

  useEffect(() => {
    refetch();
    accountDataRefetch();
    accountStatsDataRefetch();
  }, [refetch, accountDataRefetch, accountStatsDataRefetch]);

  useEffect(() => {
    if (
      accountData?.account?.package?.numListings !== null &&
      accountData?.account?.package?.numListings !== undefined &&
      accountStatsData?.accountStats?.totalActiveListings !== null &&
      accountStatsData?.accountStats?.totalActiveListings !== undefined
    ) {
      if (
        accountStatsData.accountStats?.totalActiveListings <
        accountData.account?.package?.numListings
      ) {
        setCanCreateListing(true);
      }
    }
  }, [accountData, accountStatsData]);

  if (error) {
    console.error(error);
  }

  if (loading || !data || !data.listings || !accountData || !accountStatsData) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between border-b pb-3">
        <div className="flex-1 min-w-0">
          <div>
            <h2 className="text-lg leading-6 font-medium text-gray-900">
              Listings
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Overview of your listings.
            </p>
          </div>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          {data.listings?.length > 0 && (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
              onClick={() =>
                canCreateListing
                  ? modals?.setModalState('listingNewModal', true)
                  : modals?.setModalState('upgradePackageModal', true, {
                      account: accountData.account,
                    })
              }
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Add New Listing
            </button>
          )}
          {/* <Link
            to={`${paths.LISTINGS}/new/property/details`}
            type="button"
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
          >
            Add New Listing
          </Link> */}
        </div>
      </div>
      <div className="mt-8">
        <ListingGrid listings={data.listings} />
      </div>
    </div>
  );
};

export default Listings;

const LISTINGS_QUERY = gql`
  query Listings_ListingData {
    listings {
      id
      sid
      status
      property {
        address {
          street
          city
          region
          country
          lat
          lng
        }
        media {
          sid
          filePath
          type
          context
          embedProvider
          embedId
          embedUrl
          embedType
        }
      }
    }
  }
`;

export const GET_ACCOUNT = gql`
  query Listings_AccountData($accountId: String!) {
    account(accountId: $accountId) {
      id
      package {
        packageId
        group
        name
        numListings
        numUsers
        flags
      }
      integrations {
        facebook {
          userId
          status
          accessToken
          pages {
            id
            name
            accessToken
          }
        }
      }
    }
  }
`;

const GET_ACCOUNT_STATS = gql`
  query Listings_AccountStatsData {
    accountStats {
      totalListings
      totalActiveListings
      subscribed
      subscriptionName
    }
  }
`;
